import * as React from 'react'
import PropTypes from 'prop-types'
import { CustomPropTypes } from '../../support'
import type { DivForwardRef } from '../../types'
import { useDsUiDataProps } from '../../theming'
import { PopoverStrategy } from '../Popover'
import { MenuButton } from './MenuButton'
import { MenuCustomItem } from './MenuCustomItem'
import { MenuGroup } from './MenuGroup'
import { MenuItem } from './MenuItem'
import { MenuItemDisclosure } from './MenuItemDisclosure'
import { MenuItemFileInput } from './MenuItemFileInput'
import { MenuItemWithSubmenu } from './MenuItemWithSubmenu'
import { MenuWithState } from './MenuWithState'
import { MenuContextProvider, MenuContextProviderProps } from './MenuContext'
import { menuUIGlass, MenuUIGlass } from './MenuUI'

export const menuLocations = ['above', 'below', 'before', 'after'] as const
export type MenuLocation = (typeof menuLocations)[number]

export const menuAlignments = ['start', 'center', 'end'] as const
export type MenuAlignment = (typeof menuAlignments)[number]

export interface MenuProps {
  /**
   * The alignment of the Menu along the edge of its anchor element.
   *
   * For locations 'above' and 'below':
   * - 'start': aligns the Menu and the anchor element to the start of the container
   * - 'center': centers the Menu along the width of the anchor element
   * - 'end': aligns the Menu and the anchor element to the end of the container
   *
   * For locations 'before' and 'after':
   * - 'start': top-aligns the Menu and the anchor element
   * - 'center': centers the Menu along the height of the anchor element
   * - 'end': bottom-aligns the Menu and the anchor element
   */
  alignment?: MenuAlignment
  /**
   * The Element around which the Menu will be positioned.
   */
  anchor?: HTMLElement | null
  /**
   * The aria pattern determines which roles are assigned to
   * the underlying elements.
   *
   * When passed 'menu' roles will follow an aria menu pattern.
   * This pattern is only appropriate when creating an application
   * that behaves like a native desktop application e.g. Google Docs
   *
   * When passed 'disclosure' the underlying Menu components will
   * not receive a role.  For most web application 'menus' this is
   * the most appropriate choice.
   */
  ariaPattern?: MenuContextProviderProps['ariaPattern']
  /**
   * The 'children' prop accepts arbitrary nodes, however the recommendation is to
   * use Menu.Group(s).  When using Menu.Item or its variants you *must* render them
   * within a Menu.Group.
   */
  children: React.ReactNode
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * A React ref to assign to the HTML node representing the Menu element.
   */
  forwardedRef?: DivForwardRef
  /**
   * The glass effect to apply to the Menu.
   */
  glass?: MenuUIGlass
  /**
   * The preferred location of the Menu relative to its anchor element.
   */
  location?: MenuLocation
  /**
   * Determines whether the Menu will adjust its location when it starts to overlap
   * its anchor element (by default it will "flip" to the opposite side of the
   * anchor element).
   */
  locationFixed?: boolean
  /**
   * Max height of the menu in any accepted numeric CSS unit.
   */
  maxHeight?: string
  /**
   * Automatically calculates the max height of the menu. Makes menu scrollable if it does not have
   * enough height to fully open.
   */
  maxHeightAuto?: boolean
  /**
   * By default, the minimum width of the Menu will be the width of its anchor element.
   *
   * Set this prop to `false` in order to remove this constraint.
   */
  minWidth?: boolean
  /**
   * A function to invoke when the Menu animation out completes
   */
  onHideComplete?: () => void
  /**
   * A function to invoke when the Menu animation in completes
   */
  onShowComplete?: () => void
  /**
   * Passed to underlying Popover component's onFirstUpdate prop. Will be called after the Popover
   * has positioned the element the first time.
   */
  onVisible?: () => void
  /**
   * When a Menu is displayed without providing an Element for the 'anchor' prop,
   * apply the CSS property { position: static; } to the Menu.
   *
   * The presence of this prop has no effect when an Element is provided for 'anchor'.
   *
   * DETAILS:
   * The CSS positioning properties for the Menu component will be updated in a
   * future major release.
   *  - currently: if a Menu is displayed without providing an Element for the
   *    'anchor' prop, it is positioned with the CSS properties:
   *    { position: absolute; top: 0; inset-inline-start: 0; z-index: 200; }
   *  - upcoming [FUTURE MAJOR RELEASE]: if a Menu is displayed without providing
   *    an Element for the 'anchor' prop, none of the above-referenced CSS properties
   *    will be applied, it will be up to the consumer to appropriately position
   *    the Menu (or its wrapper)
   *  - recommended: if you are displaying a Menu without providing an Element for
   *    the 'anchor' prop, you can provide the boolean prop 'positionStatic' to apply
   *    the CSS property { position: static; } to the Menu (ignoring the values
   *    that are being set for 'top', 'inset-inline-start', 'z-index')
   */
  positionStatic?: boolean
  /**
   * When the menu becomes visible its width is determined by the content. With
   * `preserveWidth` it will take the initial width and keep it as a minimum for as
   * long as the menu is open.
   *
   * This can be useful when menu's content changes rapidly, as in a searchable list.
   * It removes unnecessary resizing to minimize distraction for the user.
   */
  preserveWidth?: boolean
  /**
   * The default strategy for Menu is to use absolute positioning.  In some DOM structures
   * it may be necessary to use 'fixed' to bust out of layout/positioning rules established
   * by the Menu's ancestors.
   */
  strategy?: PopoverStrategy
  /**
   * Display the Menu.
   */
  visible?: boolean
}

/**
 * Menus provide a list of related actions in a limited space.
 */
export function Menu({
  alignment = 'start',
  ariaPattern = 'menu',
  glass,
  location = 'below',
  locationFixed = false,
  maxHeightAuto = false,
  minWidth = true,
  onHideComplete,
  onShowComplete,
  positionStatic = false,
  preserveWidth = false,
  strategy = 'absolute',
  visible = false,
  ...restProps
}: MenuProps) {
  const dsUiDataProps = useDsUiDataProps(Menu)

  return (
    <MenuContextProvider ariaPattern={ariaPattern}>
      <MenuWithState
        {...dsUiDataProps}
        {...restProps}
        visible={visible}
        alignment={alignment}
        glass={glass}
        location={location}
        locationFixed={locationFixed}
        maxHeightAuto={maxHeightAuto}
        minWidth={minWidth}
        onHideComplete={onHideComplete}
        onShowComplete={onShowComplete}
        positionStatic={positionStatic}
        preserveWidth={preserveWidth}
        strategy={strategy}
      />
    </MenuContextProvider>
  )
}

Menu.propTypes = {
  alignment: PropTypes.oneOf(menuAlignments),
  anchor: CustomPropTypes.Element,
  children: PropTypes.node.isRequired,
  'data-.*': PropTypes.string,
  forwardedRef: CustomPropTypes.ReactRef,
  glass: PropTypes.oneOf(menuUIGlass),
  location: PropTypes.oneOf(menuLocations),
  locationFixed: PropTypes.bool,
  maxHeight: PropTypes.string,
  maxHeightAuto: PropTypes.bool,
  minWidth: PropTypes.bool,
  onHideComplete: PropTypes.func,
  onShowComplete: PropTypes.func,
  positionStatic: PropTypes.bool,
  preserveWidth: PropTypes.bool,
  strategy: PropTypes.oneOf(['absolute', 'fixed']),
  visible: PropTypes.bool,
}

Menu.Button = MenuButton
Menu.CustomItem = MenuCustomItem
Menu.Group = MenuGroup
Menu.Item = MenuItem
Menu.ItemDisclosure = MenuItemDisclosure
Menu.ItemFileInput = MenuItemFileInput
Menu.ItemWithSubmenu = MenuItemWithSubmenu
